import { supabase } from "@/lib/supabase";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import type { DateRangeOption } from "@/components/clicks/DateRangeFilter";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import type { Json, TablesInsert } from "@/types/database";
import {
  EMPTY_SPYSERP_FILTER_OPTIONS,
  mapSpyserpSegmentRow,
  spyserpFilterOptionsSchema,
  spyserpGlobalAlertsSchema,
  spyserpInsightsSchema,
  type Segment,
  type SpyserpDomainStatsResult,
  type SpyserpFilterOptions,
  type SpyserpGlobalAlerts,
  type SpyserpInsightsData,
  type SpyserpResolvedDateRange,
  type SpyserpUrlStat,
  type SpyserpUrlStatsResult,
} from "@/types/spyserp";
import { parseRpcOrFallback } from "@/lib/parse-rpc";

export interface SpyserpDateRangeParams {
  dateRange: DateRangeOption;
  customFrom?: string;
  customTo?: string;
}

export interface SpyserpUrlStatsQueryParams {
  page: number;
  pageSize: number;
  sortCol: string;
  sortDesc: boolean;
  activeFilters: UrlActiveFilters;
  dateRange: DateRangeOption;
  customFrom?: string;
  customTo?: string;
}

export interface SpyserpDomainStatsQueryParams {
  page: number;
  pageSize: number;
  sortCol: string;
  sortDesc: boolean;
  activeFilters: UrlActiveFilters;
  startDate: string;
  endDate: string;
}

export interface SaveSpyserpSegmentParams {
  id?: string;
  payload: TablesInsert<"spyserp_segments">;
}

export interface SpyserpDomainKeywordsParams {
  domain: string;
  startDate: string;
  endDate: string;
  rankLimit?: number;
}

class SpySerpService {
  private static instance: SpySerpService;

  private constructor() {}

  public static getInstance(): SpySerpService {
    if (!SpySerpService.instance) {
      SpySerpService.instance = new SpySerpService();
    }
    return SpySerpService.instance;
  }

  public async getFilterOptions(): Promise<SpyserpFilterOptions> {
    const { data, error } = await supabase.rpc("get_spyserp_filter_options");
    if (error) throw error;
    return parseRpcOrFallback(
      spyserpFilterOptionsSchema,
      data,
      EMPTY_SPYSERP_FILTER_OPTIONS,
      "get_spyserp_filter_options",
    );
  }

  private async fetchSiteTypesByDomains(
    domains: string[],
  ): Promise<Record<string, string | null>> {
    if (domains.length === 0) return {};

    const { data, error } = await supabase
      .from("domains")
      .select("domain, site_type")
      .in("domain", domains);

    if (error) {
      console.error("Failed to load site types for SpySerp rows:", error);
      return {};
    }

    return Object.fromEntries(
      (data ?? []).map((row) => [row.domain, row.site_type]),
    );
  }

  private async attachSiteTypes<T extends { domain: string }>(
    stats: T[],
  ): Promise<(T & { site_type: string | null })[]> {
    const uniqueDomains = [
      ...new Set(stats.map((row) => row.domain).filter(Boolean)),
    ];
    const siteTypeByDomain = await this.fetchSiteTypesByDomains(uniqueDomains);

    return stats.map((row) => ({
      ...row,
      site_type: siteTypeByDomain[row.domain] ?? null,
    }));
  }

  public async resolveDateRange(
    params: SpyserpDateRangeParams,
  ): Promise<SpyserpResolvedDateRange> {
    const { dateRange, customFrom, customTo } = params;
    const { current } = getDateRangeDates(
      dateRange,
      new Date(),
      customFrom,
      customTo,
    );

    let startDate = current.start;
    if (!startDate) {
      const { data: minDate } = await supabase
        .from("spyserp_rankings")
        .select("check_date")
        .order("check_date", { ascending: true })
        .limit(1)
        .single();
      startDate = minDate?.check_date ?? "2020-01-01";
    }

    return { startDate, endDate: current.end };
  }

  public async getUrlStats(
    params: SpyserpUrlStatsQueryParams,
    resolved: SpyserpResolvedDateRange,
  ): Promise<SpyserpUrlStatsResult> {
    const { page, pageSize, sortCol, sortDesc, activeFilters } = params;
    const dbFrom = (page - 1) * pageSize;
    const dbTo = dbFrom + pageSize - 1;

    const { data, error, count } = await supabase
      .rpc(
        "get_spyserp_url_stats",
        {
          p_start_date: resolved.startDate,
          p_end_date: resolved.endDate,
          p_filters: activeFilters as Json,
        },
        { count: "exact" },
      )
      .order(sortCol, { ascending: !sortDesc })
      .range(dbFrom, dbTo);

    if (error) throw error;

    const stats = await this.attachSiteTypes(data ?? []);

    return {
      stats,
      totalCount: count ?? 0,
    };
  }

  public async getDomainStats(
    params: SpyserpDomainStatsQueryParams,
  ): Promise<SpyserpDomainStatsResult> {
    const {
      page,
      pageSize,
      sortCol,
      sortDesc,
      activeFilters,
      startDate,
      endDate,
    } = params;

    const dbFrom = (page - 1) * pageSize;
    const dbTo = dbFrom + pageSize - 1;

    const { data, error, count } = await supabase
      .rpc(
        "get_spyserp_domain_stats",
        {
          p_start_date: startDate,
          p_end_date: endDate,
          p_filters: activeFilters as Json,
        },
        { count: "exact" },
      )
      .order(sortCol, { ascending: !sortDesc })
      .range(dbFrom, dbTo);

    if (error) throw error;

    const stats = await this.attachSiteTypes(data ?? []);

    return {
      stats,
      totalCount: count ?? 0,
    };
  }

  public async getGlobalAlerts(
    resolved: SpyserpResolvedDateRange,
  ): Promise<SpyserpGlobalAlerts> {
    const { data, error } = await supabase.rpc("get_spyserp_global_alerts", {
      p_start_date: resolved.startDate,
      p_end_date: resolved.endDate,
    });

    if (error) throw error;

    const parsed = spyserpGlobalAlertsSchema.safeParse(data);
    if (!parsed.success) {
      throw new Error("Invalid global alerts response");
    }

    return parsed.data;
  }

  public async getInsights(
    resolved: SpyserpResolvedDateRange,
  ): Promise<SpyserpInsightsData> {
    const { data, error } = await supabase.rpc("get_spyserp_insights", {
      p_start_date: resolved.startDate,
      p_end_date: resolved.endDate,
    });

    if (error) throw error;

    const parsed = spyserpInsightsSchema.safeParse(data);
    if (!parsed.success) {
      throw new Error("Invalid insights response");
    }

    return parsed.data;
  }

  public async getSegments(): Promise<Segment[]> {
    const { data, error } = await supabase
      .from("spyserp_segments")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapSpyserpSegmentRow);
  }

  public async saveSegment({
    id,
    payload,
  }: SaveSpyserpSegmentParams): Promise<void> {
    if (id) {
      const { error } = await supabase
        .from("spyserp_segments")
        .update(payload)
        .eq("id", id);
      if (error) throw error;
      return;
    }

    const { error } = await supabase.from("spyserp_segments").insert([payload]);
    if (error) throw error;
  }

  public async deleteSegment(id: string): Promise<void> {
    const { error } = await supabase
      .from("spyserp_segments")
      .delete()
      .eq("id", id);
    if (error) throw error;
  }

  public async getDomainKeywords(
    params: SpyserpDomainKeywordsParams,
  ): Promise<SpyserpUrlStat[]> {
    let query = supabase.rpc("get_spyserp_url_stats", {
      p_start_date: params.startDate,
      p_end_date: params.endDate,
      p_filters: {
        domain: {
          mode: "include",
          values: [params.domain],
        },
      } as Json,
    });

    if (params.rankLimit) {
      query = query.lte("current_rank", params.rankLimit);
    }

    const { data, error } = await query
      .order("current_rank", { ascending: true })
      .limit(100);

    if (error) throw error;
    return this.attachSiteTypes(data ?? []);
  }
}

export const spySerpService = SpySerpService.getInstance();
